"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Header from "@/components/Header";
import Stepper from "@/components/Stepper";
import FormField, { Input, Select, Textarea } from "@/components/FormField";
import { useAuth } from "@/contexts/AuthContext";
import { generateProtocolo } from "@/lib/store";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  FileText,
  Upload,
  Download,
  Info,
} from "lucide-react";

const STEPS = [
  { number: 1, title: "Adequabilidade" },
  { number: 2, title: "Características" },
  { number: 3, title: "Responsáveis" },
  { number: 4, title: "Profissionais" },
  { number: 5, title: "Edificações" },
  { number: 6, title: "Cálculos" },
  { number: 7, title: "Áreas" },
  { number: 8, title: "Recuos" },
  { number: 9, title: "Documentação" },
  { number: 10, title: "Rascunho" },
  { number: 11, title: "Emissão" },
];

export default function EmissaoPage() {
  const { user } = useAuth();
  const router = useRouter();
  const [step, setStep] = useState(1);
  const [emitido, setEmitido] = useState(false);
  const [protocolo, setProtocolo] = useState("");

  const [form, setForm] = useState({
    // Adequabilidade
    adq_protocolo: "",
    adq_usarExistente: "nao",
    adq_endereco: "",
    adq_bairro: "",
    adq_cep: "",
    adq_zoneamento: "",
    adq_areaTerreno: "",
    adq_testada: "",
    adq_usoSolo: "",

    // Características
    car_tipoObra: "",
    car_statusObra: "",
    car_numPavimentos: "",
    car_numSubsolos: "",
    car_descricao: "",
    car_incorporacao: "nao",

    // Responsáveis
    resp_tipoPessoa: "fisica",
    resp_cpfCnpj: "",
    resp_nome: "",
    resp_email: "",
    resp_telefone: "",
    resp_mesmoTerreno: "sim",
    resp_cpfTerreno: "",
    resp_nomeTerreno: "",
    resp_vinculoLegal: "",
    resp_cpfLegal: "",
    resp_nomeLegal: "",

    // Profissionais
    prof_nomeArq: "",
    prof_creaArq: "",
    prof_artArq: "",
    prof_nomeCalculo: "",
    prof_creaCalculo: "",
    prof_artCalculo: "",
    prof_nomeExec: "",
    prof_creaExec: "",
    prof_artExec: "",

    // Edificações
    edif_licAmbiental: "nao",
    edif_protocoloAmb: "",
    edif_planoResiduos: "nao",
    edif_projetoIncendio: "nao",
    edif_numeracao: "",

    // Cálculos
    calc_areaTerreno: "",
    calc_areaConstruida: "",
    calc_areaOcupacao: "",
    calc_areaComum: "",
    calc_areaSubsolo: "",
    calc_taxaOcupacao: "",
    calc_indiceAprov: "",
    calc_areaPermeavel: "",
    calc_taxaPermeab: "",

    // Recuos e Altimetria
    rec_frontal: "",
    rec_lateralEsq: "",
    rec_lateralDir: "",
    rec_fundos: "",
    rec_entreBlocos: "",
    alt_edificacao: "",
    alt_terreno: "",
    alt_estruturas: "",
    alt_esgotamento: "",
    alt_tipoEsgoto: "",

    // Documentos (names only for UI)
    doc_projetoArq: "",
    doc_artProjeto: "",
    doc_artCalculo: "",
    doc_artExecucao: "",
    doc_matricula: "",
    doc_iptu: "",
    doc_docPessoal: "",
    doc_outros: "",
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const nextStep = () => {
    if (step < 11) setStep(step + 1);
    window.scrollTo(0, 0);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
    window.scrollTo(0, 0);
  };

  const handleEmitir = () => {
    const p = generateProtocolo();
    setProtocolo(p);
    setEmitido(true);
  };

  if (!user) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <AlertTriangle size={48} className="text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Acesso Necessário
          </h2>
          <p className="text-gray-500 mb-6">
            Para solicitar um alvará de construção, você precisa estar logado no
            sistema.
          </p>
          <button
            onClick={() => router.push("/login")}
            className="bg-primary text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-dark transition"
          >
            Fazer Login
          </button>
        </div>
      </div>
    );
  }

  if (emitido) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 bg-success-light rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={40} className="text-success" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Alvará Emitido com Sucesso!
          </h2>
          <p className="text-gray-500 mb-2">Seu alvará foi emitido com o protocolo:</p>
          <p className="text-2xl font-mono font-bold text-primary mb-8">
            {protocolo}
          </p>
          <div className="bg-primary-light border border-primary/20 rounded-xl p-6 mb-8 text-left">
            <h3 className="font-semibold text-primary mb-3 flex items-center gap-2">
              <Info size={18} />
              Informações Importantes
            </h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>
                &bull; O alvará tem validade de <strong>5 anos</strong> a partir da data de emissão.
              </li>
              <li>
                &bull; Haverá monitoramento da documentação inserida. Em caso de equívocos sanáveis,
                será concedido prazo de <strong>30 dias corridos</strong> para correção.
              </li>
              <li>
                &bull; Os projetos devem ser disponibilizados na obra para fiscalização com a tarja de verificação.
              </li>
              <li>
                &bull; Caso sejam constatadas informações incorretas, o alvará poderá ser cancelado em
                até <strong>5 dias corridos</strong>.
              </li>
            </ul>
          </div>
          <div className="flex justify-center gap-4">
            <button className="flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-dark transition">
              <Download size={18} />
              Baixar Alvará
            </button>
            <button
              onClick={() => router.push("/dashboard")}
              className="flex items-center gap-2 border border-gray-300 px-6 py-3 rounded-xl font-semibold text-gray-600 hover:bg-gray-50 transition"
            >
              Ver Meus Alvarás
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Emissão de Alvará de Construção
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Preencha todas as etapas para solicitar seu alvará autodeclaratório
          </p>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            <strong>Atenção:</strong> Confira as informações antes de prosseguir.
            Não é possível retornar à etapa anterior após avançar. Não há análise
            prévia — a responsabilidade pelas informações é do requerente e dos
            profissionais técnicos.
          </p>
        </div>

        <Stepper steps={STEPS} currentStep={step} />

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 lg:p-8">
          {/* STEP 1 - Adequabilidade */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="border-b pb-4 mb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                  Consulta de Adequabilidade
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Informe os dados de adequabilidade locacional do empreendimento
                </p>
              </div>

              <FormField label="Já possui Consulta de Adequabilidade?">
                <Select
                  name="adq_usarExistente"
                  value={form.adq_usarExistente}
                  onChange={handleChange}
                >
                  <option value="nao">Não - Solicitar nova consulta</option>
                  <option value="sim">Sim - Informar protocolo existente</option>
                </Select>
              </FormField>

              {form.adq_usarExistente === "sim" && (
                <FormField label="Número do Protocolo" required>
                  <Input
                    name="adq_protocolo"
                    value={form.adq_protocolo}
                    onChange={handleChange}
                    placeholder="Informe o número do protocolo"
                  />
                </FormField>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <FormField label="Endereço do Empreendimento" required>
                  <Input
                    name="adq_endereco"
                    value={form.adq_endereco}
                    onChange={handleChange}
                    placeholder="Rua, número, complemento"
                  />
                </FormField>
                <FormField label="Bairro" required>
                  <Input
                    name="adq_bairro"
                    value={form.adq_bairro}
                    onChange={handleChange}
                    placeholder="Bairro"
                  />
                </FormField>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <FormField label="CEP" required>
                  <Input
                    name="adq_cep"
                    value={form.adq_cep}
                    onChange={handleChange}
                    placeholder="00000-000"
                  />
                </FormField>
                <FormField label="Zoneamento" required>
                  <Select
                    name="adq_zoneamento"
                    value={form.adq_zoneamento}
                    onChange={handleChange}
                  >
                    <option value="">Selecione</option>
                    <option value="ZR1">ZR1 - Zona Residencial 1</option>
                    <option value="ZR2">ZR2 - Zona Residencial 2</option>
                    <option value="ZC">ZC - Zona Comercial</option>
                    <option value="ZM">ZM - Zona Mista</option>
                    <option value="ZI">ZI - Zona Industrial</option>
                    <option value="ZPA">ZPA - Zona de Proteção Ambiental</option>
                    <option value="ZEE">ZEE - Zona Especial</option>
                  </Select>
                </FormField>
                <FormField label="Uso do Solo" required>
                  <Select
                    name="adq_usoSolo"
                    value={form.adq_usoSolo}
                    onChange={handleChange}
                  >
                    <option value="">Selecione</option>
                    <option value="residencial_unifamiliar">Residencial Unifamiliar</option>
                    <option value="residencial_multifamiliar">Residencial Multifamiliar</option>
                    <option value="comercial">Comercial</option>
                    <option value="misto">Misto</option>
                    <option value="industrial">Industrial</option>
                    <option value="institucional">Institucional</option>
                  </Select>
                </FormField>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <FormField label="Área do Terreno (m²)" required>
                  <Input
                    name="adq_areaTerreno"
                    type="number"
                    value={form.adq_areaTerreno}
                    onChange={handleChange}
                    placeholder="0,00"
                  />
                </FormField>
                <FormField label="Testada do Terreno (m)" required>
                  <Input
                    name="adq_testada"
                    type="number"
                    value={form.adq_testada}
                    onChange={handleChange}
                    placeholder="0,00"
                  />
                </FormField>
              </div>
            </div>
          )}

          {/* STEP 2 - Características */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="border-b pb-4 mb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                  Características do Empreendimento
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Informe as características gerais da construção
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <FormField label="Tipo de Obra" required>
                  <Select
                    name="car_tipoObra"
                    value={form.car_tipoObra}
                    onChange={handleChange}
                  >
                    <option value="">Selecione</option>
                    <option value="construcao_nova">Construção Nova</option>
                    <option value="reforma">Reforma</option>
                    <option value="ampliacao">Ampliação</option>
                    <option value="demolicao">Demolição</option>
                    <option value="regularizacao">Regularização</option>
                  </Select>
                </FormField>
                <FormField
                  label="Status da Obra"
                  required
                  hint="Obra não iniciada: fundações não finalizadas. Em andamento: sem habitabilidade. Concluída: em condições de uso."
                >
                  <Select
                    name="car_statusObra"
                    value={form.car_statusObra}
                    onChange={handleChange}
                  >
                    <option value="">Selecione</option>
                    <option value="nao_iniciada">Não iniciada</option>
                    <option value="em_andamento">Em andamento</option>
                    <option value="concluida">Concluída</option>
                  </Select>
                </FormField>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <FormField
                  label="Número de Pavimentos"
                  required
                  hint="Incluindo térreo e pavimentos de estacionamento, excluindo subsolos"
                >
                  <Input
                    name="car_numPavimentos"
                    type="number"
                    value={form.car_numPavimentos}
                    onChange={handleChange}
                    placeholder="0"
                  />
                </FormField>
                <FormField
                  label="Número de Subsolos"
                  required
                  hint="Pavimentos abaixo do nível do solo"
                >
                  <Input
                    name="car_numSubsolos"
                    type="number"
                    value={form.car_numSubsolos}
                    onChange={handleChange}
                    placeholder="0"
                  />
                </FormField>
              </div>

              <FormField label="Descrição da Obra" required>
                <Textarea
                  name="car_descricao"
                  value={form.car_descricao}
                  onChange={handleChange}
                  placeholder="Descreva brevemente a obra a ser realizada..."
                />
              </FormField>

              <FormField label="Trata-se de Incorporação Imobiliária?">
                <Select
                  name="car_incorporacao"
                  value={form.car_incorporacao}
                  onChange={handleChange}
                >
                  <option value="nao">Não</option>
                  <option value="sim">Sim</option>
                </Select>
              </FormField>

              {form.car_incorporacao === "sim" && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-700">
                    <strong>Nota:</strong> Em caso de incorporação, será
                    obrigatório a inclusão do quadro da NBR de acordo com a
                    legislação vigente.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* STEP 3 - Responsáveis */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="border-b pb-4 mb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                  Dados dos Responsáveis
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Informe os dados do proprietário do empreendimento e do
                  responsável legal
                </p>
              </div>

              <h3 className="font-semibold text-gray-800 bg-gray-50 p-3 rounded-lg">
                Proprietário do Empreendimento
              </h3>

              <div className="grid md:grid-cols-2 gap-4">
                <FormField label="Tipo de Pessoa" required>
                  <Select
                    name="resp_tipoPessoa"
                    value={form.resp_tipoPessoa}
                    onChange={handleChange}
                  >
                    <option value="fisica">Pessoa Física</option>
                    <option value="juridica">Pessoa Jurídica</option>
                  </Select>
                </FormField>
                <FormField
                  label={
                    form.resp_tipoPessoa === "fisica" ? "CPF" : "CNPJ"
                  }
                  required
                >
                  <Input
                    name="resp_cpfCnpj"
                    value={form.resp_cpfCnpj}
                    onChange={handleChange}
                    placeholder={
                      form.resp_tipoPessoa === "fisica"
                        ? "000.000.000-00"
                        : "00.000.000/0000-00"
                    }
                  />
                </FormField>
              </div>

              <div className="grid md:grid-cols-1 gap-4">
                <FormField label="Nome Completo / Razão Social" required>
                  <Input
                    name="resp_nome"
                    value={form.resp_nome}
                    onChange={handleChange}
                    placeholder="Nome completo ou razão social"
                  />
                </FormField>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <FormField label="E-mail" required>
                  <Input
                    name="resp_email"
                    type="email"
                    value={form.resp_email}
                    onChange={handleChange}
                    placeholder="email@exemplo.com"
                  />
                </FormField>
                <FormField label="Telefone" required>
                  <Input
                    name="resp_telefone"
                    value={form.resp_telefone}
                    onChange={handleChange}
                    placeholder="(00) 00000-0000"
                  />
                </FormField>
              </div>

              <FormField label="Os dados do proprietário do terreno são os mesmos?">
                <Select
                  name="resp_mesmoTerreno"
                  value={form.resp_mesmoTerreno}
                  onChange={handleChange}
                >
                  <option value="sim">Sim</option>
                  <option value="nao">Não</option>
                </Select>
              </FormField>

              {form.resp_mesmoTerreno === "nao" && (
                <div className="space-y-4 pl-4 border-l-4 border-primary-light">
                  <h4 className="font-medium text-gray-700">
                    Proprietário do Terreno
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <FormField label="CPF/CNPJ do Proprietário do Terreno" required>
                      <Input
                        name="resp_cpfTerreno"
                        value={form.resp_cpfTerreno}
                        onChange={handleChange}
                      />
                    </FormField>
                    <FormField label="Nome do Proprietário do Terreno" required>
                      <Input
                        name="resp_nomeTerreno"
                        value={form.resp_nomeTerreno}
                        onChange={handleChange}
                      />
                    </FormField>
                  </div>
                </div>
              )}

              <h3 className="font-semibold text-gray-800 bg-gray-50 p-3 rounded-lg mt-8">
                Responsável Legal
              </h3>

              <div className="grid md:grid-cols-2 gap-4">
                <FormField label="Vínculo do Responsável Legal" required>
                  <Select
                    name="resp_vinculoLegal"
                    value={form.resp_vinculoLegal}
                    onChange={handleChange}
                  >
                    <option value="">Selecione</option>
                    <option value="proprietario">Proprietário</option>
                    <option value="procurador">Procurador</option>
                    <option value="representante">Representante Legal</option>
                    <option value="responsavel_tecnico">Responsável Técnico</option>
                  </Select>
                </FormField>
                <FormField label="CPF do Responsável Legal" required>
                  <Input
                    name="resp_cpfLegal"
                    value={form.resp_cpfLegal}
                    onChange={handleChange}
                    placeholder="000.000.000-00"
                  />
                </FormField>
              </div>

              <FormField label="Nome do Responsável Legal" required>
                <Input
                  name="resp_nomeLegal"
                  value={form.resp_nomeLegal}
                  onChange={handleChange}
                  placeholder="Nome completo do responsável legal"
                />
              </FormField>
            </div>
          )}

          {/* STEP 4 - Profissionais */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="border-b pb-4 mb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                  Profissionais Técnicos
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Informe os dados dos profissionais técnicos responsáveis pelo
                  projeto e execução
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-700">
                  Os profissionais técnicos devem possuir registro no CREA/CAU e
                  cadastro técnico municipal ativo.
                </p>
              </div>

              {[
                {
                  title: "Responsável pelo Projeto Arquitetônico",
                  prefix: "prof_nomeArq",
                  crea: "prof_creaArq",
                  art: "prof_artArq",
                },
                {
                  title: "Responsável pelo Cálculo Estrutural",
                  prefix: "prof_nomeCalculo",
                  crea: "prof_creaCalculo",
                  art: "prof_artCalculo",
                },
                {
                  title: "Responsável pela Execução da Obra",
                  prefix: "prof_nomeExec",
                  crea: "prof_creaExec",
                  art: "prof_artExec",
                },
              ].map((prof) => (
                <div key={prof.title} className="space-y-4 mb-6">
                  <h3 className="font-semibold text-gray-800 bg-gray-50 p-3 rounded-lg">
                    {prof.title}
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <FormField label="Nome do Profissional" required>
                      <Input
                        name={prof.prefix}
                        value={(form as Record<string, string>)[prof.prefix]}
                        onChange={handleChange}
                        placeholder="Nome completo"
                      />
                    </FormField>
                    <FormField label="CREA/CAU" required>
                      <Input
                        name={prof.crea}
                        value={(form as Record<string, string>)[prof.crea]}
                        onChange={handleChange}
                        placeholder="Nº do registro"
                      />
                    </FormField>
                    <FormField label="ART/RRT" required>
                      <Input
                        name={prof.art}
                        value={(form as Record<string, string>)[prof.art]}
                        onChange={handleChange}
                        placeholder="Nº da ART/RRT"
                      />
                    </FormField>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* STEP 5 - Edificações */}
          {step === 5 && (
            <div className="space-y-6">
              <div className="border-b pb-4 mb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                  Dados da Edificação
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Informe dados sobre licenciamento ambiental, resíduos e
                  prevenção contra incêndio
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <FormField label="Possui Licenciamento Ambiental?" required>
                  <Select
                    name="edif_licAmbiental"
                    value={form.edif_licAmbiental}
                    onChange={handleChange}
                  >
                    <option value="nao">Não</option>
                    <option value="sim">Sim</option>
                    <option value="dispensado">Dispensado</option>
                    <option value="em_andamento">Em andamento</option>
                  </Select>
                </FormField>
                {(form.edif_licAmbiental === "sim" ||
                  form.edif_licAmbiental === "em_andamento") && (
                  <FormField label="Protocolo do Licenciamento Ambiental" required>
                    <Input
                      name="edif_protocoloAmb"
                      value={form.edif_protocoloAmb}
                      onChange={handleChange}
                      placeholder="Nº do protocolo"
                    />
                  </FormField>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <FormField label="Possui Plano de Gerenciamento de Resíduos?" required>
                  <Select
                    name="edif_planoResiduos"
                    value={form.edif_planoResiduos}
                    onChange={handleChange}
                  >
                    <option value="nao">Não</option>
                    <option value="sim">Sim</option>
                    <option value="dispensado">Dispensado</option>
                  </Select>
                </FormField>
                <FormField
                  label="Possui Projeto Contra Incêndio e Pânico?"
                  required
                >
                  <Select
                    name="edif_projetoIncendio"
                    value={form.edif_projetoIncendio}
                    onChange={handleChange}
                  >
                    <option value="nao">Não</option>
                    <option value="sim">Sim</option>
                    <option value="dispensado">Dispensado</option>
                  </Select>
                </FormField>
              </div>

              <FormField label="Numeração do Empreendimento">
                <Input
                  name="edif_numeracao"
                  value={form.edif_numeracao}
                  onChange={handleChange}
                  placeholder="Número de identificação do empreendimento"
                />
              </FormField>
            </div>
          )}

          {/* STEP 6 - Cálculos (Informações adicionais) */}
          {step === 6 && (
            <div className="space-y-6">
              <div className="border-b pb-4 mb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                  Informações de Cálculo
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Informe os índices urbanísticos do empreendimento
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <FormField label="Área do Terreno (m²)" required>
                  <Input
                    name="calc_areaTerreno"
                    type="number"
                    value={form.calc_areaTerreno}
                    onChange={handleChange}
                    placeholder="0,00"
                  />
                </FormField>
                <FormField label="Área Construída Total (m²)" required>
                  <Input
                    name="calc_areaConstruida"
                    type="number"
                    value={form.calc_areaConstruida}
                    onChange={handleChange}
                    placeholder="0,00"
                  />
                </FormField>
                <FormField label="Área de Ocupação (m²)" required hint="Projeção horizontal da edificação">
                  <Input
                    name="calc_areaOcupacao"
                    type="number"
                    value={form.calc_areaOcupacao}
                    onChange={handleChange}
                    placeholder="0,00"
                  />
                </FormField>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <FormField label="Área Comum Construída (m²)">
                  <Input
                    name="calc_areaComum"
                    type="number"
                    value={form.calc_areaComum}
                    onChange={handleChange}
                    placeholder="0,00"
                  />
                </FormField>
                <FormField label="Área Total do Subsolo (m²)">
                  <Input
                    name="calc_areaSubsolo"
                    type="number"
                    value={form.calc_areaSubsolo}
                    onChange={handleChange}
                    placeholder="0,00"
                  />
                </FormField>
                <FormField label="Área Permeável (m²)" required>
                  <Input
                    name="calc_areaPermeavel"
                    type="number"
                    value={form.calc_areaPermeavel}
                    onChange={handleChange}
                    placeholder="0,00"
                  />
                </FormField>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <FormField label="Taxa de Ocupação (%)" required>
                  <Input
                    name="calc_taxaOcupacao"
                    type="number"
                    value={form.calc_taxaOcupacao}
                    onChange={handleChange}
                    placeholder="0,00"
                  />
                </FormField>
                <FormField label="Índice de Aproveitamento" required>
                  <Input
                    name="calc_indiceAprov"
                    type="number"
                    value={form.calc_indiceAprov}
                    onChange={handleChange}
                    placeholder="0,00"
                  />
                </FormField>
                <FormField label="Taxa de Permeabilidade (%)" required>
                  <Input
                    name="calc_taxaPermeab"
                    type="number"
                    value={form.calc_taxaPermeab}
                    onChange={handleChange}
                    placeholder="0,00"
                  />
                </FormField>
              </div>
            </div>
          )}

          {/* STEP 7 - Quadro de Áreas */}
          {step === 7 && (
            <div className="space-y-6">
              <div className="border-b pb-4 mb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                  Quadro de Áreas
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Preencha o detalhamento das áreas por tipologia do
                  empreendimento
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-700">
                  <strong>Tipologia:</strong> Define as diferentes unidades
                  (casa, apartamento, sala). A área total é calculada
                  automaticamente com base nas informações fornecidas.
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">
                        Tipologia
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">
                        Área Parcial (m²)
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">
                        Área Não Computável (m²)
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">
                        Qtd. Unidades
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">
                        Área Total (m²)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t">
                      <td className="px-4 py-3">
                        <Input placeholder="Ex: Casa, Apt" />
                      </td>
                      <td className="px-4 py-3">
                        <Input type="number" placeholder="0,00" />
                      </td>
                      <td className="px-4 py-3">
                        <Input type="number" placeholder="0,00" />
                      </td>
                      <td className="px-4 py-3">
                        <Input type="number" placeholder="0" />
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-gray-500">Calculado</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <button className="text-sm text-primary font-medium hover:text-primary-dark transition">
                + Adicionar tipologia
              </button>
            </div>
          )}

          {/* STEP 8 - Recuos e Altimetria */}
          {step === 8 && (
            <div className="space-y-6">
              <div className="border-b pb-4 mb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                  Recuos e Altimetria
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Informe os recuos da edificação e dados de altimetria
                </p>
              </div>

              <h3 className="font-semibold text-gray-800 bg-gray-50 p-3 rounded-lg">
                Quadro de Recuos (metros)
              </h3>

              <div className="grid md:grid-cols-3 gap-4">
                <FormField label="Recuo Frontal (m)" required>
                  <Input
                    name="rec_frontal"
                    type="number"
                    value={form.rec_frontal}
                    onChange={handleChange}
                    placeholder="0,00"
                  />
                </FormField>
                <FormField label="Recuo Lateral Esquerdo (m)" required>
                  <Input
                    name="rec_lateralEsq"
                    type="number"
                    value={form.rec_lateralEsq}
                    onChange={handleChange}
                    placeholder="0,00"
                  />
                </FormField>
                <FormField label="Recuo Lateral Direito (m)" required>
                  <Input
                    name="rec_lateralDir"
                    type="number"
                    value={form.rec_lateralDir}
                    onChange={handleChange}
                    placeholder="0,00"
                  />
                </FormField>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <FormField label="Recuo de Fundos (m)" required>
                  <Input
                    name="rec_fundos"
                    type="number"
                    value={form.rec_fundos}
                    onChange={handleChange}
                    placeholder="0,00"
                  />
                </FormField>
                <FormField label="Recuo Entre Blocos (m)" hint="Somente se houver mais de uma torre">
                  <Input
                    name="rec_entreBlocos"
                    type="number"
                    value={form.rec_entreBlocos}
                    onChange={handleChange}
                    placeholder="0,00"
                  />
                </FormField>
              </div>

              <h3 className="font-semibold text-gray-800 bg-gray-50 p-3 rounded-lg mt-8">
                Altimetria
              </h3>

              <div className="grid md:grid-cols-3 gap-4">
                <FormField
                  label="Altura da Edificação (m)"
                  required
                  hint="Até a laje de forro do último pavimento"
                >
                  <Input
                    name="alt_edificacao"
                    type="number"
                    value={form.alt_edificacao}
                    onChange={handleChange}
                    placeholder="0,00"
                  />
                </FormField>
                <FormField
                  label="Nível do Terreno (m)"
                  required
                  hint="Cota de nível de implantação"
                >
                  <Input
                    name="alt_terreno"
                    type="number"
                    value={form.alt_terreno}
                    onChange={handleChange}
                    placeholder="0,00"
                  />
                </FormField>
                <FormField
                  label="Altura das Estruturas (m)"
                  hint="Caixas d'água, antenas, etc."
                >
                  <Input
                    name="alt_estruturas"
                    type="number"
                    value={form.alt_estruturas}
                    onChange={handleChange}
                    placeholder="0,00"
                  />
                </FormField>
              </div>

              <h3 className="font-semibold text-gray-800 bg-gray-50 p-3 rounded-lg mt-8">
                Sistema de Esgotamento Sanitário
              </h3>

              <div className="grid md:grid-cols-2 gap-4">
                <FormField label="A região é abastecida pelo sistema público de esgoto?" required>
                  <Select
                    name="alt_esgotamento"
                    value={form.alt_esgotamento}
                    onChange={handleChange}
                  >
                    <option value="">Selecione</option>
                    <option value="sim">Sim</option>
                    <option value="nao">Não</option>
                  </Select>
                </FormField>
                {form.alt_esgotamento === "nao" && (
                  <FormField label="Tipo de Solução Adotada" required>
                    <Select
                      name="alt_tipoEsgoto"
                      value={form.alt_tipoEsgoto}
                      onChange={handleChange}
                    >
                      <option value="">Selecione</option>
                      <option value="fossa_septica">Fossa Séptica</option>
                      <option value="fossa_sumidouro">Fossa + Sumidouro</option>
                      <option value="estacao_tratamento">Estação de Tratamento</option>
                      <option value="outro">Outro</option>
                    </Select>
                  </FormField>
                )}
              </div>
            </div>
          )}

          {/* STEP 9 - Documentação */}
          {step === 9 && (
            <div className="space-y-6">
              <div className="border-b pb-4 mb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                  Documentação
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Anexe os documentos necessários em formato PDF
                </p>
              </div>

              {[
                {
                  key: "doc_projetoArq",
                  label: "Projeto Arquitetônico",
                  required: true,
                },
                {
                  key: "doc_artProjeto",
                  label: "ART/RRT do Projeto Arquitetônico",
                  required: true,
                },
                {
                  key: "doc_artCalculo",
                  label: "ART/RRT do Cálculo Estrutural",
                  required: true,
                },
                {
                  key: "doc_artExecucao",
                  label: "ART/RRT da Execução da Obra",
                  required: true,
                },
                {
                  key: "doc_matricula",
                  label: "Matrícula do Imóvel / Escritura",
                  required: true,
                },
                {
                  key: "doc_iptu",
                  label: "IPTU ou Certidão de Cadastro Imobiliário",
                  required: true,
                },
                {
                  key: "doc_docPessoal",
                  label: "Documento Pessoal (CPF/RG ou CNPJ/Contrato Social)",
                  required: true,
                },
                {
                  key: "doc_outros",
                  label: "Outros Documentos",
                  required: false,
                },
              ].map((doc) => (
                <div
                  key={doc.key}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:border-primary/50 transition"
                >
                  <div className="flex items-center gap-3">
                    <FileText size={20} className="text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        {doc.label}
                        {doc.required && (
                          <span className="text-danger ml-1">*</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-400">
                        {(form as Record<string, string>)[doc.key]
                          ? (form as Record<string, string>)[doc.key]
                          : "Nenhum arquivo selecionado"}
                      </p>
                    </div>
                  </div>
                  <label className="cursor-pointer bg-primary-light text-primary px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary hover:text-white transition flex items-center gap-2">
                    <Upload size={16} />
                    Enviar
                    <input
                      type="file"
                      accept=".pdf"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setForm({
                            ...form,
                            [doc.key]: file.name,
                          });
                        }
                      }}
                    />
                  </label>
                </div>
              ))}
            </div>
          )}

          {/* STEP 10 - Rascunho / Revisão */}
          {step === 10 && (
            <div className="space-y-6">
              <div className="border-b pb-4 mb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                  Revisão do Requerimento
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Verifique todas as informações antes de prosseguir
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-amber-800">
                  <strong>Atenção:</strong> Após confirmar, não será possível
                  modificar os dados informados. Caso encontre algum erro,
                  cancele e inicie um novo requerimento.
                </p>
              </div>

              {[
                {
                  title: "Adequabilidade",
                  items: [
                    { label: "Endereço", value: form.adq_endereco },
                    { label: "Bairro", value: form.adq_bairro },
                    { label: "CEP", value: form.adq_cep },
                    { label: "Zoneamento", value: form.adq_zoneamento },
                    { label: "Uso do Solo", value: form.adq_usoSolo },
                    { label: "Área do Terreno", value: form.adq_areaTerreno ? `${form.adq_areaTerreno} m²` : "" },
                  ],
                },
                {
                  title: "Características",
                  items: [
                    { label: "Tipo de Obra", value: form.car_tipoObra },
                    { label: "Status", value: form.car_statusObra },
                    { label: "Pavimentos", value: form.car_numPavimentos },
                    { label: "Subsolos", value: form.car_numSubsolos },
                    { label: "Descrição", value: form.car_descricao },
                  ],
                },
                {
                  title: "Responsáveis",
                  items: [
                    { label: "Proprietário", value: form.resp_nome },
                    { label: "CPF/CNPJ", value: form.resp_cpfCnpj },
                    { label: "E-mail", value: form.resp_email },
                    { label: "Responsável Legal", value: form.resp_nomeLegal },
                  ],
                },
                {
                  title: "Profissionais",
                  items: [
                    { label: "Projeto Arq.", value: form.prof_nomeArq },
                    { label: "Cálculo", value: form.prof_nomeCalculo },
                    { label: "Execução", value: form.prof_nomeExec },
                  ],
                },
                {
                  title: "Cálculos",
                  items: [
                    { label: "Área Construída", value: form.calc_areaConstruida ? `${form.calc_areaConstruida} m²` : "" },
                    { label: "Taxa de Ocupação", value: form.calc_taxaOcupacao ? `${form.calc_taxaOcupacao}%` : "" },
                    { label: "Índice Aprov.", value: form.calc_indiceAprov },
                    { label: "Altura", value: form.alt_edificacao ? `${form.alt_edificacao} m` : "" },
                  ],
                },
              ].map((section) => (
                <div
                  key={section.title}
                  className="border border-gray-200 rounded-xl overflow-hidden"
                >
                  <div className="bg-gray-50 px-4 py-3">
                    <h3 className="font-semibold text-gray-800 text-sm">
                      {section.title}
                    </h3>
                  </div>
                  <div className="p-4 grid md:grid-cols-2 gap-3">
                    {section.items.map((item) => (
                      <div key={item.label}>
                        <p className="text-xs text-gray-400">{item.label}</p>
                        <p className="text-sm text-gray-800 font-medium">
                          {item.value || "-"}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* STEP 11 - Emissão */}
          {step === 11 && (
            <div className="space-y-6">
              <div className="border-b pb-4 mb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                  Emissão do Alvará
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Confirme as informações e emita seu alvará
                </p>
              </div>

              {/* Confirmação Responsável Legal */}
              <div className="border border-gray-200 rounded-xl p-5">
                <h3 className="font-semibold text-gray-800 mb-3">
                  Confirmação do Responsável Legal
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  O Responsável Legal e os Profissionais Técnicos devem
                  confirmar as informações prestadas neste requerimento.
                </p>
                <div className="space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" className="mt-1 w-4 h-4 accent-primary" />
                    <span className="text-sm text-gray-600">
                      Declaro que as informações prestadas são verdadeiras e que
                      me responsabilizo pela veracidade dos dados informados,
                      estando ciente das penalidades previstas em lei.
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" className="mt-1 w-4 h-4 accent-primary" />
                    <span className="text-sm text-gray-600">
                      Confirmo que o projeto atende a todos os parâmetros
                      urbanísticos e ambientais vigentes no município de Aracati.
                    </span>
                  </label>
                </div>
                <div className="mt-4 flex items-center gap-2 bg-success-light p-3 rounded-lg">
                  <Check size={18} className="text-success" />
                  <span className="text-sm text-success font-medium">
                    Confirmação pendente
                  </span>
                </div>
              </div>

              {/* Pagamento */}
              <div className="border border-gray-200 rounded-xl p-5">
                <h3 className="font-semibold text-gray-800 mb-3">
                  Pagamento da(s) Taxa(s) - DAM
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Este campo estará disponível após a confirmação de todos os
                  responsáveis. O sistema atualizará a situação para
                  &quot;PAGO&quot; quando a(s) taxa(s) for(em) compensada(s).
                </p>
                <div className="bg-amber-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Taxa de Alvará de Construção
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Valor calculado conforme legislação municipal
                      </p>
                    </div>
                    <span className="text-lg font-bold text-amber-700">
                      R$ 150,00
                    </span>
                  </div>
                  <button className="mt-4 w-full bg-amber-500 text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-amber-600 transition">
                    Gerar DAM para Pagamento
                  </button>
                </div>
              </div>

              {/* Botão Emitir */}
              <button
                onClick={handleEmitir}
                className="w-full bg-primary text-white py-4 rounded-xl text-lg font-bold hover:bg-primary-dark transition shadow-lg"
              >
                EMITIR ALVARÁ DE CONSTRUÇÃO
              </button>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={prevStep}
              disabled={step === 1}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition ${
                step === 1
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <ArrowLeft size={18} />
              Anterior
            </button>

            {step < 11 && (
              <button
                onClick={nextStep}
                className="flex items-center gap-2 bg-primary text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary-dark transition"
              >
                Próximo
                <ArrowRight size={18} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
